// What each session in a search's reach was, one row a session, for Explore's
// list: when it ran, on which machine, with which agent and model, from one
// read of the newest sessions the search matches (from the frames, without
// counting each one's requests). Who ran each and what review found on it are
// read only for the rows a list shows (useTraceDetails), each landing as it
// comes, so the rows are drawn before either.
//
// A session's person is its machine's: the person the directory assigns the
// machine to (getMachinePeople), else the account the machine is registered
// to, else no one known, and the machine stands for its person
// (`personIsMachine`). Its group is the directory person's department
// (`group`, "" without one; `assigned` says whether the directory assigns the
// machine to anyone). A session whose frames name no machine (a connector's
// capture) takes the one its prompts name (getTraceMachines). What review
// found is its findings counted by severity (getTraceFindings).
//
// The full row needs a read of each session's facts (getSessionFacts), which
// the analytics backend does not serve yet; served, it is the row.
import { useMemo } from "react";
import { HTTP_METHODS } from "./apiClient.js";
import { useQuery } from "./dataClient.js";
import { dimensionMeta, scopeStepLabel, useLabels } from "./dimensionDefs.js";
import { useKeyedReads } from "./keyedReads.js";

const SEV = ["critical", "high", "medium", "low"];
const NONE = [];
// Ids a read at most: the planner takes 1024 values a list.
const CHUNK = 500;

// A machine's name (its hostname, `machineName`, once known), and the account
// it is registered to: without the machine's own name a Windows account
// carries (HOST\name), an email by the part before the @; a generic account
// says no one in particular.
const GENERIC_ACCOUNT = /^(user|users|admin|administrator|root|default|guest|ubuntu|ec2-user)$/i;
export function ownerOf(registeredUser) {
  const account = String(registeredUser || "").trim();
  const name = account.slice(account.lastIndexOf("\\") + 1).split("@")[0].trim();
  return name && !GENERIC_ACCOUNT.test(name) ? name : "";
}
function machineOf(id) {
  if (!id) return { machine: "", machineName: "", owner: "" };
  const label = scopeStepLabel({ dim: "endpoints", id });
  const machineName = label && label !== id ? label : "";
  const machine = machineName || `machine ${id.slice(0, 8)}`;
  return { machine, machineName, owner: ownerOf(dimensionMeta("endpoints", id)?.registeredUser) };
}

// Who ran a session on a machine: the person the directory assigns it to,
// else its owner as recorded, else no one known, and the machine stands for
// its person (said as a machine).
export function personOf({ assigned = "", owner = "", machine = "", onMachine = true }) {
  const person = assigned || owner || machine;
  return { person, personIsMachine: !assigned && !owner && onMachine && !!machine };
}

export function useSessionFacts(scope, range) {
  const served = Object.prototype.hasOwnProperty.call(HTTP_METHODS, "getSessionFacts");
  const full = useQuery("getSessionFacts", { scopePath: scope.scopePath, query: scope.steer, range }, { enabled: served && scope.ready });
  // What there is today: the sessions the search matched (the scope's own
  // match, with its review clauses applied), else the range's newest.
  const match = useQuery("getSessionMatches", { scopePath: [], range, query: "", withRequests: false }, { enabled: !served && scope.ready && !scope.q });
  const listed = useMemo(() => (scope.q ? (scope.ready && scope.sessions ? { sessions: scope.sessions, capped: scope.capped } : null) : match.data), [scope.q, scope.ready, scope.sessions, scope.capped, match.data]);
  const failed = scope.q ? scope.match?.error : match.error;
  const rows = listed?.sessions || NONE;
  const endpoints = useMemo(() => [...new Set(rows.map((s) => s.endpointId).filter(Boolean))], [rows]);
  // Machines' names and owners arrive after the rows; each arrival redraws.
  const labelsTick = useLabels(endpoints.length ? [{ dim: "endpoints", ids: endpoints }] : NONE);
  // The rows are the last search's (or range's) while the new one is read,
  // and `settled` says that read failed (an error is kept while a newer read
  // loads, so only one that has stopped loading counts).
  const previous = served ? !!full.previousParams : scope.q ? !!scope.match?.previousParams : !!match.previousParams;
  const settled = served ? !!full.error && !full.loading : !!failed && !(scope.q ? scope.match?.loading : match.loading);
  return useMemo(() => {
    if (served) {
      return { status: full.error ? "error" : full.data ? "done" : "loading", sessions: full.data?.sessions || [], partial: false, previous, failed: settled };
    }
    if (!listed) return { status: failed ? "error" : "loading", sessions: [], partial: true };
    const sessions = rows.map((s) => {
      const m = machineOf(s.endpointId);
      return {
        id: s.id, machineId: s.endpointId || null, machine: m.machine, machineName: m.machineName,
        agent: s.agent || "", model: s.model || "",
        requests: s.requests || 0, startMs: s.firstMs, lastMs: s.lastMs,
      };
    });
    // `previous` with `failed`: the new search's read failed and these rows are the last one's.
    return { status: "done", sessions, partial: true, capped: !!listed.capped, previous, failed: settled };
  }, [served, full.data, full.error, listed, failed, rows, labelsTick, previous, settled]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Who ran each of `rows` and what review found on it, read for those rows
// alone and kept for every list that reads the same, each landing as it
// comes: id -> traceDetail. A machine keeps the person first read for it, so
// a narrower list does not move its sessions to another person.
export function useTraceDetails(rows, range) {
  const ids = useMemo(() => rows.map((r) => r.id), [rows]);
  const unplaced = useMemo(() => rows.filter((r) => !r.machineId).map((r) => r.id), [rows]);
  const people = useKeyedReads("getMachinePeople", ids, { range }, { keep: true, chunk: CHUNK });
  const machines = useKeyedReads("getTraceMachines", unplaced, { range }, { chunk: CHUNK });
  const findings = useKeyedReads("getTraceFindings", ids, { range }, { chunk: CHUNK });
  const placed = useMemo(() => [...new Set(unplaced.map((id) => machines.data?.[String(id).toLowerCase()]).filter(Boolean))], [unplaced, machines]);
  const labelsTick = useLabels(placed.length ? [{ dim: "endpoints", ids: placed }] : NONE);
  return useMemo(
    () => new Map(rows.map((r) => [r.id, traceDetail(r, { people, machines, findings })])),
    [rows, people, machines, findings, labelsTick], // eslint-disable-line react-hooks/exhaustive-deps
  );
}

/**
 * One row's details from the reads so far (pure): its person and machine
 * fields once its machine's people are read (and, where its frames name no
 * machine, the machine its prompts name), and { bySev, findings, sev } once
 * its findings are counted. A part not yet in is left out of it.
 */
export function traceDetail(row, { people, machines, findings }) {
  const key = String(row.id).toLowerCase();
  const out = {};
  const machineId = row.machineId || machines.data?.[key] || "";
  if (people.settled(row.id) && (row.machineId || machines.settled(row.id))) {
    const m = machineOf(machineId);
    const who = machineId ? people.data?.[machineId] : null;
    Object.assign(out, {
      machineId: machineId || null, machine: m.machine, machineName: m.machineName,
      ...personOf({ assigned: who?.name || "", owner: m.owner, machine: m.machine, onMachine: !!machineId }),
      group: who?.department || "", assigned: !!who?.name,
    });
  }
  if (findings.settled(row.id)) {
    const counts = findings.data?.[key] || {};
    const bySev = Object.fromEntries(SEV.filter((s) => counts[s]).map((s) => [s, counts[s]]));
    Object.assign(out, { bySev, findings: Object.values(counts).reduce((n, k) => n + k, 0), sev: SEV.find((s) => bySev[s]) || null });
  }
  return out;
}
